import { SidebarFeatureEnum } from 'config/types/sidebar'
import { TournamentDetailsByGame, TournamentType } from 'config/types/tournament'
import { useCountDown } from 'hooks/useCountdown'
import { useRequest } from 'hooks/useRequest'
import { useRouter } from 'hooks/useRouter'
import Column from 'layout/Components/Column'
import { RowMiddle } from 'layout/Components/Row'
import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import TournamentService from 'services/TournamentService'
import { useAppDispatch } from 'state'
import { useToggleSidebar } from 'state/app/hooks'
import { updateActiveTournament } from 'state/tournament/actions'
import { useTournamentList } from 'state/tournament/hooks'
import styled from 'styled-components'
import colors from 'theme/ui/colors'
import Box from 'UIKit/Box/Box'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image'
import Text from 'UIKit/Text'
import TransText from 'UIKit/Text/TransText'
import { delayed } from 'utils'

const TournamentOverview: React.FC<{ tournamentsActive: TournamentType[] } & BoxProps> = ({
  tournamentsActive,
  ...props
}) => {
  const [tournamentDetails, setTournamentDetails] = useState<TournamentDetailsByGame>()
  const router = useRouter()
  const { execute } = useRequest()
  const gameSlug = router.asPath.split('?')[0]
  const ref = useCountDown(tournamentDetails?.expiredTime, (s, m, h, d) => {
    const padZero = (num: number) => (num < 10 ? `0${num}` : num)
    return `${padZero(d)} : ${padZero(h)} : ${padZero(m)} : ${padZero(s)}`
  })
  const { openSidebar } = useToggleSidebar()
  const dispatch = useAppDispatch()

  useEffect(() => {
    const fetch = async () => {
      const response = await execute(TournamentService.getActiveTournamentByGameSlug(encodeURI(gameSlug.slice(1))))
      if (response?.data) {
        setTournamentDetails(response.data)
        return
      }
      setTournamentDetails(null)
    }

    fetch()
  }, [gameSlug, tournamentsActive])

  const handleOpenTournament = async () => {
    dispatch(updateActiveTournament({ data: { tournamentId: tournamentDetails.id } }))
    await delayed(500)
    openSidebar(SidebarFeatureEnum.Tournament)
  }
  return (
    tournamentDetails && (
      <StyledContainer
        onClick={handleOpenTournament}
        px="12px"
        height="65px"
        width={['100%', , , 'fit-content']}
        my={['12px', , , '0px']}
        border={`1px solid ${colors.primaryAlt}`}
        borderRadius="6px"
        background="rgba(255, 133, 203, 0.08)"
        {...props}
      >
        <Box width={['32px', , '40px']} height={['32px', , '40px']}>
          <Image src="/images/tournament/tournament-icon.png" width={40} height={40} alt="tournament-icon" />
        </Box>
        <Column ml="8px">
          <TransText fontSize="16px" fontWeight="800" letterSpacing="-0.32px">
            {tournamentDetails.name}
          </TransText>
          <RowMiddle mt="4px">
            <RowMiddle>
              <Text fontSize="14px" fontWeight="400" color="primaryAlt">
                <Trans>Prize Pool</Trans>:
              </Text>
              <Text fontSize="14px" fontWeight="600" color="primaryAlt" ml="4px">
                {tournamentDetails.prizePool}
              </Text>
            </RowMiddle>
            <Box height="12px" borderLeft={`1px solid ${colors.textAlt5}`} mx="4px" />
            <Text ref={ref} fontSize="14px" fontWeight={500} letterSpacing="-0.28px" minWidth="100px" />
          </RowMiddle>
        </Column>
      </StyledContainer>
    )
  )
}

const TournamentDetailsOfGame = ({ ...props }: BoxProps) => {
  const tournamentList = useTournamentList()

  return (
    tournamentList.activeTournaments && (
      <TournamentOverview tournamentsActive={tournamentList.activeTournaments} {...props} />
    )
  )
}

const StyledContainer = styled(RowMiddle)`
  cursor: pointer;
`

export default TournamentDetailsOfGame
